/* istanbul ignore file */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import {
  Col, Form, Row, Table,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import ErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/ErrorWrapper/ErrorWrapper';
import { parseChannelKey } from '../../../../../utility/channelUtils';

// const nonAdjustableChannelTypes = [
//   ChannelType.SWITCH.id,
//   ChannelType.ANALOG_COMPUTED.id,
//   ChannelType.FLOW.id,
//   ChannelType.SPEED.id,
//   ChannelType.PARTICULATE_MATTER.id,
//   ChannelType.PARTICULATE_TEMP_FAULT.id,
//   ChannelType.EXTREME_TEMPERATURE_FAULT.id,
//   ChannelType.ACTIVE_POWER.id,
//     ChannelType.
// ];

const adjustableChannelTypes = [
  ChannelType.TEMPERATURE.id,
  ChannelType.HUMIDITY.id,
  ChannelType.HEAT_INDEX.id,
  ChannelType.DEW_POINT.id,
  ChannelType.VOLTAGE.id,
  ChannelType.ANALOG_COMPUTED.id,
  ChannelType.SPEED.id,
  ChannelType.PARTICULATE_MATTER.id,
];

const SettingsMainContent = ({
  label,
  handleLabelChange,
  channelGroup,
  channelValues,
  handleAdjustChange,
  errorList,
  preferencesDisplay,
  readOnly,
}) => {
  const cgInstance = ChannelService.getChannelGroupTypeByValueId(
    parseChannelKey(channelGroup.channelKey).channelGroupType,
  );

  // Local mirror of errors so we can clear them on user interaction
  // without waiting for the parent / redux to update.
  const [dismissedFields, setDismissedFields] = useState(new Set());

  // Whenever a fresh errorList arrives from the parent (e.g. after a new
  // save attempt), reset the dismissed set so new errors are visible.
  useEffect(() => {
    setDismissedFields(new Set());
  }, [errorList]);

  const visibleErrorList = errorList.filter((err) => !dismissedFields.has(err.field));

  const clearFieldError = (field) => {
    setDismissedFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  const onLabelChange = (value) => {
    clearFieldError('label');
    handleLabelChange(value);
  };

  const onAdjustChange = (key, value, idx) => {
    clearFieldError(`channels[${idx}].adjust`);
    handleAdjustChange(key, value);
  };

  const fieldList = React.useMemo(() => {
    const fields = ['label'];
    (channelGroup.channels || []).forEach((_, idx) => {
      fields.push(`channels[${idx}].adjust`);
    });
    return fields;
  }, [channelGroup]);

  return (
    <FormErrorHandler errorList={visibleErrorList} fieldList={fieldList}>
      {/* Wrap in a plain div so AltErrorWrapper's <span> gets block context via d-block */}
      <div className="d-block mb-2">
        <AltErrorWrapper fieldId="label">
          <Form.Control
            id="sensorLabel"
            type="text"
            value={label}
            onChange={(e) => onLabelChange(e.target.value)}
          />
        </AltErrorWrapper>
      </div>
      <Form.Text className="text-muted">
        <small className="ms-2">
          Max chars 50
        </small>
      </Form.Text>
      <Row className="my-3 ps-1">
        <Col>
          <b className="pe-2">Sensor Type</b>
          {' '}
          {cgInstance.displayName}
        </Col>
      </Row>
      <Table striped className="table">
        <thead style={{ backgroundColor: 'rgb(88, 88, 88)', color: 'white', borderBottom: 'none' }}>
          <tr>
            <th>Sensor Channel</th>
            <th>Adjustment</th>
          </tr>
        </thead>
        <tbody>
          {channelGroup.channels.map((channel, idx) => {
            const parsedKey = parseChannelKey(channel.channelKey);
            const channelInstance = ChannelService.getChannelGroupChannel(
              parsedKey.channelGroupType, parsedKey.channelIndex, parsedKey.channelType,
            );
            const scale = preferencesDisplay.temperatureScale || 'C';
            const units = channelInstance.getUnits({ scale });
            return (
              <tr key={channel.channelKey}>
                <td>{channelInstance.displayName}</td>
                <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                  {adjustableChannelTypes.includes(parsedKey.channelType) && (
                  <Form.Group className="mb-0">
                    <ErrorWrapper fieldId={`channels[${idx}].adjust`}>
                      <div className="col-12 d-flex align-items-center p-0">
                        <Form.Control
                          type="text"
                          className="w-75"
                          value={channelValues[channel.channelKey]}
                          onChange={(e) => onAdjustChange(channel.channelKey, e.target.value, idx)}
                          readOnly={readOnly}
                          disabled={readOnly}
                        />
                        <span className="ms-2">{units}</span>
                      </div>
                    </ErrorWrapper>
                  </Form.Group>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </FormErrorHandler>
  );
};

SettingsMainContent.defaultProps = {
  readOnly: false,
  errorList: [],
};

SettingsMainContent.propTypes = {
  label: PropTypes.string.isRequired,
  handleLabelChange: PropTypes.func.isRequired,
  channelGroup: PropTypes.object.isRequired,
  channelValues: PropTypes.object.isRequired,
  handleAdjustChange: PropTypes.func.isRequired,
  errorList: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
    code: PropTypes.string,
  })),
  preferencesDisplay: PropTypes.shape({
    temperatureScale: PropTypes.string,
    unitOfSpeed: PropTypes.string,
  }).isRequired,
  readOnly: PropTypes.bool,
};

export default SettingsMainContent;
